import React from 'react';
import Box from '@nubank/nuds-web/components/Box/Box';
import styled from 'styled-components';
import Image from '@nubank/nuds-web/components/Image/Image';
import Typography from '@nubank/nuds-web/components/Typography/Typography';
import Grid from '@nubank/nuds-web/components/Grid/Grid';

const StyledContainerGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
  /* stylelint-disable declaration-block-no-redundant-longhand-properties */
  grid-template-areas: 'image' 'title' 'detailRows';
  gap: 0;

  @media (width >= 1150px) {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto 1fr;
    grid-template-areas: 
    'image title'
    'detailRows detailRows';
    column-gap: 25px;
    row-gap: 40px;
  }
`;

const ImageContainer = styled.div`
  grid-area: image;

  @media (width >= 768px) {
    img {
      border-radius: 12px;
    }
  }
`;

const TitleContainer = styled.div`
  grid-area: title;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding-left: 32px;
  padding-right: 32px;
  margin-top: -100px;

  @media (width >= 1150px) {
    padding-left: 0;
    padding-right: 0;
    margin-top: 0;
    justify-content: space-between;
    height: 120%;

    div {
      height: 224px;
    }
  }
`;

const DetailRowsContainer = styled.div`
  grid-area: detailRows;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-left: 32px;
  margin-right: 32px;
  margin-top: 20px;

  @media (width >= 1150px) {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    width: 100%;
    margin-left: 0;
    margin-right: 0;
    margin-top: 0;
    gap: 32px;
  }
`;

const StyledIconImage = styled(Image)`
  width: 49px;
  height: 49px;
  min-width: 49px;
`;

const StyledTypography = styled(Typography)`
  span {
    font-weight: bold;
  }

  @media (width >= 1151px) {
    font-size: 18px;
  }
`;

const StyledBoxTypography = styled(Typography)`
  font-size: 28px;
  font-weight: 500;
  line-height: 33.6px;
  letter-spacing: -0.03em;
  text-align: left;

  @media (width >= 768px) {
    font-size: 48px;
    line-height: 55.68px;
    text-align: center;
  }
`;

const StyledGrid = styled(Grid)`
  @media (width >= 1151px) {
    display: block;
    max-width: 1215px;
    margin: 0 auto;
  }
 
`;

function Security() {
  return (
    <Box
      tag="section"
      height="100%"
      width="100%"
      marginBottom={{ xs: '0', lg: '100px' }}
      marginTop={{ xs: '0', lg: '48px' }}
      paddingHorizontal={{ xs: '0', lg: '56px' }}
    >
      <StyledGrid
        paddingLeft={{ xs: '0', lg: '0' }}
        paddingRight={{ xs: '0', lg: '0' }}
      >

        <Grid.Child
          gridColumn={{ xs: '1 / span 4', md: '2 / span 4', lg: '2 / span 10' }}
          gridRow="1"
        >
          {/* BACK IMAGE */}

          <StyledContainerGrid>
            <ImageContainer>
              <Image
                alt="Dos personas viendo un telefono"
                srcSet={{
                  xs: 'atencion/security-mobile.jpg',
                  lg: 'atencion/security-desktop.jpg',
                }}
                height={{ xs: '85%', md: 'auto', lg: '100%' }}
                objectFit="cover"
                webP
              />
            </ImageContainer>

            {/* TITLES */}

            <TitleContainer>
              <Box
                background="#820AD1"
                paddingVertical={{ xs: '32px', lg: '42px' }}
                paddingHorizontal={{ xs: '32px', lg: '42px' }}
                borderRadius="12px"
                display="flex"
                flexDirection="column"
                justifyContent={{ xs: 'auto', lg: 'center' }}
                alignItems={{ xs: 'auto', lg: 'center' }}
              >
                <StyledBoxTypography
                  color="white"
                  variant="heading2"
                  textAlign={{ xs: 'left', lg: 'center' }}
                >
                  Así cuidamos tu dinero para darte tranquilidad
                </StyledBoxTypography>
              </Box>

            </TitleContainer>

            {/* ROW BOXES */}

            <DetailRowsContainer>
              <Box
                background="#F8F8FA"
                display="flex"
                borderRadius="12px"
                paddingHorizontal={{
                  xs: '20px',
                  lg: '24px',
                  xl: '32px',
                }}
                paddingVertical={{
                  xs: '30px',
                  lg: '24px',
                  xl: '40px',
                }}
              >
                <StyledIconImage
                  webP={false}
                  src="atencion/virtual-card-icon.svg"
                  alt="Prepara tus documentos"
                />

                <Box
                  display="flex"
                  flexDirection="column"
                  marginLeft="20px"
                >
                  <Typography
                    strong
                    marginBottom="12px"
                  >
                    Tarjetas virtuales que se adaptan a tus compras en línea
                  </Typography>
                  <StyledTypography>
                    ¡Crea múltiples tarjetas! Incluso puedes crear
                    una tarjeta que
                    {' '}
                    <span>expira en 24 horas</span>
                    {' '}
                    para compras más seguras.
                  </StyledTypography>
                </Box>
              </Box>

              <Box
                background="#F8F8FA"
                display="flex"
                borderRadius="12px"
                paddingHorizontal={{
                  xs: '20px',
                  lg: '24px',
                  xl: '32px',
                }}
                paddingVertical={{
                  xs: '30px',
                  lg: '24px',
                  xl: '40px',
                }}
              >
                <StyledIconImage
                  webP={false}
                  src="atencion/shield-icon.svg"
                  alt="Aplica desde la pagina"
                />
                <Box
                  display="flex"
                  flexDirection="column"
                  marginLeft="20px"
                >
                  <Typography strong marginBottom="12px">
                    Tecnología para cuidarte de compras inusuales
                  </Typography>
                  <StyledTypography>
                    Si vemos
                    {' '}
                    <span>un movimiento inusual, </span>
                    te pediremos confirmar que tú estás haciendo la compra.
                  </StyledTypography>
                </Box>
              </Box>

              <Box
                background="#F8F8FA"
                display="flex"
                borderRadius="12px"
                paddingHorizontal={{
                  xs: '20px',
                  lg: '24px',
                  xl: '32px',
                }}
                paddingVertical={{
                  xs: '30px',
                  lg: '24px',
                  xl: '40px',
                }}
              >
                <StyledIconImage
                  webP={false}
                  src="atencion/smile-icon.svg"
                  alt="Aumenta tus posibilidades"
                />
                <Box
                  display="flex"
                  flexDirection="column"
                  marginLeft="20px"
                >
                  <Typography strong marginBottom="12px">
                    Cuidamos el acceso a tu app con reconocimiento facial
                  </Typography>
                  <StyledTypography>
                    Para recuperar tu contraseña, usamos reconocimiento
                    facial para hacer una prueba de vida y
                    {' '}
                    <span>validar tu identidad.</span>
                  </StyledTypography>
                </Box>
              </Box>
            </DetailRowsContainer>

          </StyledContainerGrid>
        </Grid.Child>
      </StyledGrid>
    </Box>

  );
}

export default Security;
